import { createSlice } from '@reduxjs/toolkit';
import { ReducerStatus } from 'utils/constants';
import {
  fetchProgressionByCourseIdAction,
  postProgressionAction,
  postInteractiveSessionAction,
  postFileSubmissionSessionAction,
  fetchSubQuestsByQuestIdAction,
} from './progression-actions';

const progressionSlice = createSlice({
  name: 'progression',
  initialState: {
    getProgressionStatus: ReducerStatus.IDLE,
    postProgressionStatus: ReducerStatus.IDLE,
    postProgressionQuizStatus: ReducerStatus.IDLE,
    postFileSubmissionStatus: ReducerStatus.IDLE,
    totalSubQuestCount: 0,
    progressions: [],
    courseOutlines: [],
    questSubQuests: [],
  },
  reducers: {
    updateProgression(state, action) {
      const { id, ...rest } = action.payload;

      const existingItemIndex = state.progressions.findIndex(
        (item) => item.id === id
      );
      const updatedItems = [...state.progressions];

      if (existingItemIndex === -1) updatedItems.push({ id, ...rest });
      else updatedItems[existingItemIndex] = { id, ...rest };

      state.progressions = updatedItems;
    },
    updateQuestSubQuests(state, action) {
      const { questId, subQuests } = action.payload;

      const existingQuestIndex = state.questSubQuests.findIndex(
        (questSubQuest) => questSubQuest.questId === questId
      );

      if (existingQuestIndex !== -1) {
        state.questSubQuests[existingQuestIndex].subQuests = subQuests;
      } else {
        state.questSubQuests.push({
          questId,
          subQuests,
        });
      }
    },
    resetFileSubmissionStatus(state) {
      state.postFileSubmissionStatus = ReducerStatus.IDLE;
    },
    resetQuestSubQuests(state) {
      state.questSubQuests = [];
    },
  },
  extraReducers(builder) {
    builder
      // Fetch Progression By CourseId
      .addCase(fetchProgressionByCourseIdAction.pending, (state) => {
        state.getProgressionStatus = ReducerStatus.LOADING;
      })
      .addCase(fetchProgressionByCourseIdAction.fulfilled, (state, action) => {
        state.getProgressionStatus = ReducerStatus.SUCCEEDED;
        const { totalSubQuestCount, progressions, outlines } = action.payload;
        state.totalSubQuestCount = totalSubQuestCount;
        state.progressions = progressions;
        state.courseOutlines = outlines;
      })
      .addCase(fetchProgressionByCourseIdAction.rejected, (state) => {
        state.getProgressionStatus = ReducerStatus.FAILED;
      })
      // Post Progression;
      .addCase(postProgressionAction.pending, (state) => {
        state.postProgressionStatus = ReducerStatus.LOADING;
      })
      .addCase(postProgressionAction.fulfilled, (state, action) => {
        state.postProgressionStatus = ReducerStatus.SUCCEEDED;
        const { id, ...rest } = action.payload;

        const existingItemIndex = state.progressions.findIndex(
          (item) => item.id === id
        );
        const updatedItems = [...state.progressions];

        if (existingItemIndex === -1) updatedItems.push({ id, ...rest });
        else updatedItems[existingItemIndex] = { id, ...rest };

        state.progressions = updatedItems;
      })
      .addCase(postProgressionAction.rejected, (state) => {
        state.postProgressionStatus = ReducerStatus.FAILED;
      })

      // Record Interactive Session
      .addCase(postInteractiveSessionAction.pending, (state) => {
        state.postProgressionStatus = ReducerStatus.LOADING;
      })
      .addCase(postInteractiveSessionAction.fulfilled, (state, action) => {
        state.postProgressionStatus = ReducerStatus.SUCCEEDED;
        const { id, ...rest } = action.payload;

        const existingItemIndex = state.progressions.findIndex(
          (item) => item.id === id
        );
        const updatedItems = [...state.progressions];

        if (existingItemIndex === -1) updatedItems.push({ id, ...rest });
        else updatedItems[existingItemIndex] = { id, ...rest };
        state.progressions = updatedItems;
      })
      .addCase(postInteractiveSessionAction.rejected, (state) => {
        state.postProgressionStatus = ReducerStatus.FAILED;
      })

      // Record File Submission Session
      .addCase(postFileSubmissionSessionAction.pending, (state) => {
        state.postFileSubmissionStatus = ReducerStatus.LOADING;
      })
      .addCase(postFileSubmissionSessionAction.fulfilled, (state, action) => {
        state.postFileSubmissionStatus = ReducerStatus.SUCCEEDED;
        const { id, ...rest } = action.payload;

        const existingItemIndex = state.progressions.findIndex(
          (item) => item.id === id
        );
        const updatedItems = [...state.progressions];

        if (existingItemIndex === -1) updatedItems.push({ id, ...rest });
        else updatedItems[existingItemIndex] = { id, ...rest };
        state.progressions = updatedItems;
      })
      .addCase(postFileSubmissionSessionAction.rejected, (state) => {
        state.postFileSubmissionStatus = ReducerStatus.FAILED;
      })

      // Fetch SubQuests by Quest ID
      .addCase(fetchSubQuestsByQuestIdAction.pending, (state) => {
        state.getProgressionStatus = ReducerStatus.LOADING;
      })
      .addCase(fetchSubQuestsByQuestIdAction.fulfilled, (state, action) => {
        state.getProgressionStatus = ReducerStatus.SUCCEEDED;
        const { questId, subQuests } = action.payload;

        // Check quest already exists in questSubQuests
        const existingQuestIndex = state.questSubQuests.findIndex(
          (questSubQuest) => questSubQuest.questId === questId
        );

        if (existingQuestIndex !== -1) {
          state.questSubQuests[existingQuestIndex].subQuests = subQuests;
        } else {
          state.questSubQuests.push({
            questId,
            subQuests,
          });
        }
      })
      .addCase(fetchSubQuestsByQuestIdAction.rejected, (state) => {
        state.getProgressionStatus = ReducerStatus.FAILED;
      });
  },
});

export const progressionActions = progressionSlice.actions;

export const getProgressionPercentage = (state) => {
  // Add null checks to prevent TypeError
  if (
    !state.progression ||
    !state.progression.progressions ||
    state.progression.progressions.length === 0
  ) {
    return 0;
  }

  if (
    !state.progression.totalSubQuestCount ||
    state.progression.totalSubQuestCount === 0
  ) {
    return 0;
  }

  return (
    (state.progression.progressions.filter((p) => p.dateFinished).length /
      state.progression.totalSubQuestCount) *
      100 || 0
  );
};

export default progressionSlice;
